// src/components/WorkoutDetails/WorkoutDetails.js
import React, { useEffect, useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import "../style.css";
import "../../common/style.css";
import WorkoutDetailsHeader from "./WorkoutDetailsHeader";
import ExerciseList from "./ExerciseList";
import CustomFooter from "../CustomFooter.js";
import LanguageContext from "../../../context/LanguageContext";

import { convertSecondsToTimeStr, parseReps, parseExerciseData } from "../../utils/workoutDetailsUtils";

// Import data
const jsonDataEn = require("../../data/data-en.json");
const jsonDataFr = require("../../data/data-fr.json");

const WorkoutDetails = ({ footer }) => {
  const { language } = useContext(LanguageContext);
  const data = language === "en" ? jsonDataEn.data : jsonDataFr.data;

  const location = useLocation();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openIndex, setOpenIndex] = useState(null);
  const [showGifIndex, setShowGifIndex] = useState([]);
  const [exerciseStates, setExerciseStates] = useState({});
  const [showGifs, setShowGifs] = useState([]);

  // Initialize exerciseStates once exercises are loaded
  // ** New: comment modal state **
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [status, setStatus] = useState(null); // "success" | "error"
  useEffect(() => {
    if (exercises.length > 0 && Object.keys(exerciseStates).length === 0) {
      const initialStates = exercises.reduce((acc, exercise) => {
        acc[exercise.exercise_id] = {
          sets: 0,
          countdown: exercise.rest_seconds || 60,
          timerActive: false,
          completed: false,
        };
        return acc;
      }, {});
      setExerciseStates(initialStates);
    }
  }, [exercises, exerciseStates]);

  const openModal = () => setShowCommentModal(true);
  const closeModal = () => {
    setShowCommentModal(false);
    setFeedback("");
    setStatus(null);
  };

  const submitFeedback = async () => {
    if (!feedback.trim()) {
      setStatus("error");
      return;
    }
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL;
      const res = await fetch(`${API_BASE}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feedback }),
      });
      if (!res.ok) throw new Error(res.statusText);
      setStatus("success");
    } catch (err) {
      console.error("Feedback submit error:", err);
      setStatus("error");
    }
  };
  // Update timers every second
  useEffect(() => {
    if (Object.keys(exerciseStates).length === 0) return;
    const interval = setInterval(() => {
      setExerciseStates((prevStates) => {
        const newStates = { ...prevStates };
        exercises.forEach((exercise) => {
          const state = newStates[exercise.exercise_id];
          if (state.timerActive && state.countdown > 0) {
            newStates[exercise.exercise_id].countdown -= 1;
            if (newStates[exercise.exercise_id].countdown === 0) {
              if (state.sets >= exercise.sets) {
                newStates[exercise.exercise_id].completed = true;
              }
              newStates[exercise.exercise_id].timerActive = false;
            }
          }
        });
        return newStates;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [exercises, exerciseStates]);

  // Decode the query string and fetch exercise details from the server
  useEffect(() => {
    const decodeData = async () => {
      const queryParams = new URLSearchParams(location.search);
      const dataString = queryParams.get("data");
      if (!dataString) {
        setError("No workout data found.");
        setLoading(false);
        return;
      }
      try {
        const API_BASE = process.env.REACT_APP_API_BASE_URL;
        const parsedExercises = parseExerciseData(dataString);
        const uniqueExerciseIds = [...new Set(parsedExercises.map((ex) => ex.exercise_id))];
        const response = await fetch(`${API_BASE}/exercises`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(uniqueExerciseIds),
        });
        if (!response.ok) throw new Error("Failed to fetch exercises data.");
        const responseData = await response.json();
        const parsedExercisesMap = new Map(parsedExercises.map((ex) => [ex.exercise_id, ex]));
        const mergedExercises = responseData.map((exercise) => ({
          ...exercise,
          ...parsedExercisesMap.get(exercise.exercise_id),
          rest_seconds: parsedExercisesMap.get(exercise.exercise_id)?.rest_seconds || 60,
        }));
        setExercises(mergedExercises);
        setShowGifs(Array(mergedExercises.length).fill(true));
      } catch (err) {
        console.error("Error fetching workout data:", err);
        setError("Failed to load workout details.");
      } finally {
        setLoading(false);
      }
    };
    decodeData();
  }, [location.search]);

  const toggleInstructions = (index) => setOpenIndex(openIndex === index ? null : index);
  const toggleGif = (index) => {
    setShowGifs((prev) => prev.map((val, i) => (i === index ? !val : val)));
  };

  if (loading)
    return (
      <section className="hero-section">
        <div className="container hero-content">
          <div className="loader">
            <div className="loading-text">
              Loading<span className="dot">.</span>
              <span className="dot">.</span>
              <span className="dot">.</span>
            </div>
          </div>
        </div>
      </section>
    );
  if (error) return <div>{error}</div>;

  return (
    <>
      <section className="hero-section">
        <div className="container hero-content">
          <WorkoutDetailsHeader data={data.finalSlide} />
          <ExerciseList
            exercises={exercises}
            exerciseStates={exerciseStates}
            setExerciseStates={setExerciseStates}
            openIndex={openIndex}
            toggleInstructions={toggleInstructions}
            showGifIndex={null}
            toggleGif={toggleGif}
            showGifs={showGifs}
          />
        </div>
        {/* === Leave a comment button === */}
        <div className="comment-section">
          <button className="btn btn-feedback" onClick={openModal}>
            {data.feedback.button}
          </button>
        </div>
        <CustomFooter line1={footer} line2={data.footer} />
      </section>
      {/* === Modal overlay === */}
      {showCommentModal && (
        <div className="modal-backdrop-feedback">
          <div className="modal-content-feedback">
            <h3>{data.feedback.title}</h3>
            <p>{data.feedback.description}</p>
            <textarea className="form-control" rows="5" value={feedback} onChange={(e) => setFeedback(e.target.value)} />
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={closeModal}>
                {data.feedback.cancel}
              </button>
              <button className="btn btn-primary" onClick={submitFeedback}>
                {data.feedback.submit}
              </button>
            </div>
            {status === "success" && <div className="alert alert-success mt-2">{data.feedback.success}</div>}
            {status === "error" && <div className="alert alert-danger mt-2">{data.feedback.fail}</div>}
          </div>
        </div>
      )}
    </>
  );
};

export default WorkoutDetails;
